import { FormHandlerConfig, formHandler } from 'reusable/mobx-store-utils/form-handlers/new/form-handler';
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils';
import ProposalsEditStore from '../ProposalsEditStore';
import ProposalsEditRecipientsStore from '../step-1-recipients/ProposalsEditRecipientsStore';
import { ProposalAttachment, ProposalStep5Dto, ProposalStep5DtoSchema } from '@gap/backend/app-services/proposal/models/ProposalStep5Dto';
import { apiClient } from 'api/trpc/trpc-client';
import Loader from 'reusable/mobx-helpers/Loader';
import { snackbarError, snackbarSuccess } from 'reusable/mui/snackbar/snackbar';
import { Analytics, AnalyticsEvent } from 'analytics/analytics';
import ModalState from 'reusable/mobx-helpers/ModalState';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import LinkOutlined from '@mui/icons-material/LinkOutlined';
import { formatWebsiteUrl } from 'reusable/utils/validation';

// Fallback release for the upload gate. Covers the case where every file in a
// drop fails server-side validation — FileUploadStore fires no completion hook
// in that path, so without a timeout the gate sticks until page reload. 60s
// comfortably exceeds the 20s per-file validation wait in FileUploadStore.
const UPLOAD_GATE_SAFETY_TIMEOUT_MS = 60000

export default class ProposalsEditReviewStore {
   sp
   sendTestEmailLoader = new Loader(false)
   sendEmailLoader = new Loader(false)
   addingAttachmentLoader = new Loader()
   uploadingFilesLoader = new Loader(false)
   addAttachmentModal = new ModalState()
   files: File[] = []
   fields: ProposalStep5Dto = {
      personalMessage: '',
   }
   attachmentFields = {
      linkName: '',
      link: '',
      tabValue: 'fromYourDevice',
   }
   private uploadingFilesResolve: (() => void) | null = null
   private uploadingFilesSafetyTimeout: number | null = null

   constructor(sp: ProposalsEditReviewStoreParams) {
      this.sp = sp
   }

   get formHandler() {
      return formHandler(this.fields, {
         personalMessage: { type: 'string', onValueChange: () => this.onMessageChange() },
      } satisfies FormHandlerConfig)
   }

   get attachmentForm() {
      return formHandler(this.attachmentFields, {
         linkName: { type: 'string' },
         link: { type: 'string' },
         tabValue: { type: 'string', onValueChange: () => this.clearPendingAttachmentDraft() },
      } satisfies FormHandlerConfig)
   }

   get proposal() {
      return this.sp.proposalsEditStore.proposal
   }

   get attachments(): ProposalAttachment[] {
      return this.proposal.step5?.attachments ?? []
   }

   get attachmentTabOptions() {
      return [{
         id: 'fromYourDevice',
         label: 'From your device',
         icon: UploadFileOutlined,
      }, {
         id: 'shareALink',
         label: 'Share a link',
         icon: LinkOutlined,
      }]
   }

   get attachmentModalPrimaryButton() {
      return {
         name: this.addingAttachmentLoader.isLoading ? 'Adding' : 'Add',
         loading: this.addingAttachmentLoader.isLoading,
         callback: this.addAttachment,
         notCloseAutomatically: true,
      }
   }

   initialise = () => {
      this.fields.personalMessage = this.proposal.step5?.personalMessage || ''
   }

   onMessageChange = async () => {
      if (!this.formHandler.validate(ProposalStep5DtoSchema)) {
         return
      }
      // Only initialise step5 if the message is valid and it isn't already initialised
      this.proposal.step5 ??= { personalMessage: this.fields.personalMessage }
      this.proposal.step5.personalMessage = this.fields.personalMessage
      this.sp.proposalsEditStore.autoSave.updateProposal()
   }

   // FileUploadWithProgress reports validated files asynchronously after
   // upload completes. Discard if the user has switched away from the file
   // tab in the meantime — without this guard, a stale file from a cancelled
   // attempt re-populates this.files and gets attached on the next submit.
   setFile = async (files: File[]) => {
      if (this.attachmentFields.tabValue !== 'fromYourDevice') return
      this.files = files
      this.releaseUploadGate()
   }

   // Wired from attachmentForm.tabValue.onValueChange — fires after the
   // tabValue has flipped. Clears whatever the user partially staged on the
   // OLD tab so a half-completed file upload or link entry doesn't leak into
   // the submit triggered from the new tab. Also releases the upload gate
   // so a stuck "Saving..." can't outlive the tab switch.
   clearPendingAttachmentDraft = () => {
      this.files = []
      this.attachmentFields.linkName = ''
      this.attachmentFields.link = ''
      this.releaseUploadGate()
   }

   onUploadStarted = () => {
      // Raises the "upload gate" flag so sendEmail / sendTestEmail refuse to
      // fire while an attachment is still in flight. Release paths:
      //   - onFilesValidated (upload succeeded) → setFile → releaseUploadGate
      //   - onLastFileRemoved (user cancelled) → releaseUploadGate directly
      //   - getPresignedUrl threw (URL fetch failed) → releases in its catch
      //   - safety timeout below — see UPLOAD_GATE_SAFETY_TIMEOUT_MS for the
      //     all-files-rejected case where FileUploadStore fires no hook at all
      if (this.uploadingFilesResolve) return
      void this.uploadingFilesLoader.executeLoading(async () => {
         await new Promise<void>((resolve) => {
            this.uploadingFilesResolve = resolve
            this.uploadingFilesSafetyTimeout = window.setTimeout(() => {
               this.releaseUploadGate()
            }, UPLOAD_GATE_SAFETY_TIMEOUT_MS)
         })
      })
   }

   getPresignedUrl = async (file: File) => {
      try {
         const { url } = await apiClient.proposals.getAttachmentUploadUrl.query({
            proposalId: this.proposal.id,
            fileName: file.name,
         })
         return url
      } catch (error: unknown) {
         // Release the upload gate on failure so Send is not permanently blocked
         this.releaseUploadGate()
         throw error
      }
   }

   releaseUploadGate = () => {
      if (this.uploadingFilesSafetyTimeout !== null) {
         window.clearTimeout(this.uploadingFilesSafetyTimeout)
         this.uploadingFilesSafetyTimeout = null
      }
      if (this.uploadingFilesResolve) {
         this.uploadingFilesResolve()
         this.uploadingFilesResolve = null
      }
   }

   addLinkAttachment = (): ProposalAttachment | undefined => {
      // Ensure the link starts with http or https
      const link = formatWebsiteUrl(this.attachmentFields.link)

      // Validate URL shape
      try {
         new URL(link || '')
      } catch {
         this.attachmentForm.link.addError('Invalid URL')
         return
      }

      return { link, linkName: this.attachmentFields.linkName }
   }

   addAttachment = async () => {
      await this.addingAttachmentLoader.executeLoading(async () => {
         // Ensure step5 is initialised before mutating attachments
         this.proposal.step5 ??= { attachments: [] }
         const attachments: ProposalAttachment[] = this.proposal.step5.attachments ?? []

         if (this.attachmentFields.tabValue === 'fromYourDevice') {
            for (const file of this.files) {
               attachments.push({ fileName: file.name })
            }
         } else {
            const newLink = this.addLinkAttachment()
            if (!newLink) return
            attachments.push(newLink)
         }

         this.proposal.step5.attachments = [...attachments]
         this.sp.proposalsEditStore.autoSave.updateProposal()

         this.attachmentFields.link = ''
         this.attachmentFields.linkName = ''
         this.files = []
         this.addAttachmentModal.close()
      })
   }

   removeAttachment = async (attachment: ProposalAttachment) => {
      if (!this.proposal.step5?.attachments) return
      this.proposal.step5.attachments = this.proposal.step5.attachments.filter((a) => a !== attachment)
      this.sp.proposalsEditStore.autoSave.updateProposal()
   }

   sendTestEmail = async () => {
      if (this.uploadingFilesLoader.isLoading) {
         snackbarError('An attachment is still uploading. Please wait for it to finish before sending.')
         return
      }
      await this.sendTestEmailLoader.executeLoading(async () => {
         await apiClient.proposals.sendProposalEmail.mutate({
            isTestEmail: true,
            proposalId: this.proposal.id
         })
         snackbarSuccess('Test email sent successfully, please check your inbox')
      })
   }

   sendEmail = async () => {
      if (this.uploadingFilesLoader.isLoading) {
         snackbarError('An attachment is still uploading. Please wait for it to finish before sending.')
         return
      }
      await this.sendEmailLoader.executeLoading(async () => {
         await apiClient.proposals.sendProposalEmail.mutate({
            proposalId: this.proposal.id
         })
         snackbarSuccess(`Proposal successfully sent to ${this.sp.proposalsEditStore.client?.name}`)
         Analytics.emitEvent(AnalyticsEvent.proposalSent, undefined)
         await this.sp.proposalsEditStore.navigateToProposals()
      })
   }
}

interface ProposalsEditReviewStoreParams {
   proposalsEditStore: ProposalsEditStore,
   proposalsEditRecipientsStore: ProposalsEditRecipientsStore
}

export const { Provider: ProposalsEditReviewStoreProvider, useProvidedStore: useProposalsEditReviewStore } =
   createStoreContext(ProposalsEditReviewStore)
